import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SiteNavComponent } from '../site-nav/site-nav.component';

interface Recipe {
  id: string;
  title: string;
  group: string;      // filter bucket: daily|weekly|monthly|trigger|genz|dev|fintech
  cadence: string;    // badge: daily|weekly|monthly|trigger
  desc: string;
  connectors: string[];
  steps: string[];
  prompt: string;
  featured?: boolean;
  live?: boolean;    // running in production at NetXD today
}

@Component({
  selector: 'app-recipes',
  standalone: true,
  imports: [CommonModule, RouterModule, SiteNavComponent],
  templateUrl: './recipes.component.html',
  styleUrls: ['./recipes.component.css']
})
export class RecipesComponent {
  activeCategory = 'all';
  expandedId = '';
  copiedId = '';
  claudeUrl = 'https://claude.ai/new';
  demoEmail = 'anp@netxd.com';

  categories = [
    { key: 'live', label: 'Live at NetXD' },
    { key: 'all', label: 'All' },
    { key: 'daily', label: 'Daily' },
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
    { key: 'trigger', label: 'Triggered' },
    { key: 'genz', label: 'Lifestyle' },
    { key: 'dev', label: 'Developers' },
    { key: 'hiring', label: 'Hiring' },
    { key: 'fintech', label: 'Fintech' }
  ];

  recipes: Recipe[] = [
    {
      id: 'contract-invoice', title: 'Contract → Invoice', group: 'fintech', cadence: 'trigger', featured: true,
      desc: 'Read a signed contract and bill against it — with your approval before anything is finalized.',
      connectors: ['Drive', 'Stripe', 'Memory'],
      steps: [
        'When I point you at a signed contract (Drive or email), extract the client, amount, line items and billing schedule',
        'Find or create that customer in Stripe',
        'Build the invoice items from the contract terms',
        'Show me a draft to approve',
        'Finalize and send the invoice',
        'Save the result (invoice id, amount, due date, contract ref) to my memory'
      ],
      prompt: 'Save a recipe called Contract to Invoice: when I point you at a signed contract (Drive or email), extract the client, amount, line items and billing schedule; find or create that customer in Stripe; build the invoice items; show me a draft to approve; then finalize and send it, and save the invoice (id, amount, due date, contract ref) to my memory.'
    },
    {
      id: 'morning', title: 'Morning Chief-of-Staff', group: 'daily', cadence: 'daily',
      desc: 'A one-page brief on your desk before your first call.',
      connectors: ['Gmail', 'Calendar', 'Memory', 'Slack'],
      steps: [
        'Read overnight email, flag anything critical',
        "Scan today's calendar for important calls",
        'Pull the relevant notes from my memory',
        'Email me a one-page brief before my first call',
        'Post the same summary to my Slack'
      ],
      prompt: 'Save a recipe called Morning Brief: every weekday at 7am, read my overnight email and flag anything critical, scan my calendar for important calls, pull related notes from my memory, then email me a one-page brief and post it to Slack.'
    },
    {
      id: 'inbox', title: 'Inbox Triage', group: 'daily', cadence: 'daily',
      desc: 'Sort the overnight pile and pre-draft the easy replies.',
      connectors: ['Gmail', 'Memory'],
      steps: [
        'Sort overnight email into urgent / reply / read-later',
        'Draft replies for the routine ones',
        'Log any action items to my memory'
      ],
      prompt: 'Save a recipe called Inbox Triage: every weekday morning, sort my overnight email into urgent, reply and read-later, draft replies for the routine ones, and log any action items to my memory.'
    },
    {
      id: 'weekly-review', title: 'Weekly Review', group: 'weekly', cadence: 'weekly',
      desc: 'Your week, summarized and saved.',
      connectors: ['Calendar', 'Memory', 'Drive', 'Slack'],
      steps: [
        'Gather what I did this week (calendar + notes)',
        "Summarize wins, open items, and next week's priorities",
        'Save it as a doc in Google Drive',
        'Send me the link and post highlights to Slack'
      ],
      prompt: "Save a recipe called Weekly Review: every Friday at 4pm, gather what I did this week from my calendar and notes, summarize wins, open items and next week's priorities, save it to a Google Doc, and send me the link plus post highlights to Slack."
    },
    {
      id: 'spend', title: 'Spend Recap', group: 'weekly', cadence: 'weekly',
      desc: 'Where the money went, vs. your budget.',
      connectors: ['Receipts', 'Memory', 'Sheets'],
      steps: [
        "Total this week's receipts by category",
        'Compare to my budget in memory',
        "If I'm over, tell me where",
        'Append the week to my spending sheet'
      ],
      prompt: "Save a recipe called Spend Recap: every Friday, total my receipts this week by category, compare to my budget in memory, tell me where I'm over, and append the week to my spending sheet."
    },
    {
      id: 'bill-watch', title: 'Bill Watch', group: 'monthly', cadence: 'monthly',
      desc: 'Catch price creep on your recurring bills.',
      connectors: ['Gmail', 'Memory', 'Mobile'],
      steps: [
        "Find this month's bills in my email",
        'Compare prices to the last 3 months (from memory)',
        'Flag any increases or new charges',
        'Send the changes to my email + LIAM mobile'
      ],
      prompt: "Save a recipe called Bill Watch: on the 1st of each month, find my bills in email, compare prices to the last 3 months from memory, flag any increases or new charges, and send me the changes by email and to my LIAM mobile app."
    },
    {
      id: 'subs', title: 'Subscription Audit', group: 'monthly', cadence: 'monthly',
      desc: 'Kill the subscriptions you forgot about.',
      connectors: ['Gmail', 'Receipts', 'Drive'],
      steps: [
        'Find every recurring charge across email + receipts',
        "Flag anything I haven't used in 60 days",
        'Suggest what to cancel and draft the cancellation emails'
      ],
      prompt: "Save a recipe called Subscription Audit: monthly, find every recurring charge across my email and receipts, flag anything I haven't used in 60 days, and draft cancellation emails for the ones I choose."
    },
    {
      id: 'travel', title: 'Travel Autopilot', group: 'trigger', cadence: 'trigger',
      desc: 'A booking email becomes a whole trip plan.',
      connectors: ['Gmail', 'Calendar', 'Memory', 'Mobile'],
      steps: [
        'When a flight or hotel confirmation arrives, extract the details',
        'Save the trip to my memory',
        'Add calendar holds (flight, check-in, checkout)',
        'Build a packing + prep checklist and send it to my phone'
      ],
      prompt: 'Save a recipe called Travel Autopilot: whenever a flight or hotel confirmation arrives in my email, extract the details, save the trip to my memory, add calendar holds for the flight, check-in and checkout, and send a packing and prep checklist to my phone.'
    },
    {
      id: 'meeting', title: 'Meeting Follow-through', group: 'trigger', cadence: 'trigger',
      desc: 'Every meeting ends with a follow-up done.',
      connectors: ['Calendar', 'Memory', 'Gmail', 'Slack'],
      steps: [
        'After a meeting ends, pull my notes for it',
        'Draft a follow-up email with action items',
        'Post the action items to the team Slack',
        'Save the commitments to my memory'
      ],
      prompt: 'Save a recipe called Meeting Follow-through: after each meeting ends, pull my notes for it, draft a follow-up email with action items, post the action items to the team Slack, and save the commitments to my memory.'
    },
    {
      id: 'glowup', title: 'Glow-up Tracker', group: 'genz', cadence: 'weekly',
      desc: 'Your streak, your wins, a little hype.',
      connectors: ['Memory', 'Mobile'],
      steps: [
        'Pull my food and workout logs from memory',
        'Summarize my week (streak, wins, one thing to improve)',
        'Text me a short hype message'
      ],
      prompt: 'Save a recipe called Glow-up Tracker: every Sunday, pull my food and workout logs from memory, summarize my week (streak, wins, one thing to improve), and text me a short hype message.'
    },
    {
      id: 'vibe', title: 'Vibe Check (Money)', group: 'genz', cadence: 'weekly',
      desc: 'What you spent, and what’s left for the weekend.',
      connectors: ['Receipts', 'Memory'],
      steps: [
        'Total my receipts by category',
        'Compare to my budget in memory',
        "Tell me what's left for the weekend",
        'Flag anything that spiked'
      ],
      prompt: "Save a recipe called Vibe Check: every Friday, total my receipts by category, compare to my budget in memory, tell me what's left for the weekend, and flag anything that spiked."
    },
    {
      id: 'content', title: 'Content Cook', group: 'genz', cadence: 'weekly',
      desc: 'Turn saved ideas into ready-to-post drafts.',
      connectors: ['Memory', 'Drive', 'Calendar'],
      steps: [
        'Gather the post/video ideas I saved to memory',
        'Draft 3 captions with hooks',
        'Drop them in a Google Doc',
        'Remind me to film'
      ],
      prompt: 'Save a recipe called Content Cook: every Monday, gather the post and video ideas I saved to memory, draft 3 captions with hooks, drop them in a Google Doc, and remind me to film on Tuesday.'
    },
    {
      id: 'pr-digest', title: 'PR Digest', group: 'dev', cadence: 'daily',
      desc: 'Know what needs review before standup.',
      connectors: ['GitHub', 'Slack'],
      steps: [
        "Summarize my team's open pull requests",
        "Who's blocked, what's waiting on review, what's stale over 3 days",
        'Post it to our #eng Slack'
      ],
      prompt: "Save a recipe called PR Digest: every weekday at 9am, summarize my team's open pull requests — who's blocked, what's waiting on review, and what's stale over 3 days — and post it to our #eng Slack."
    },
    {
      id: 'standup', title: 'Standup Bot', group: 'dev', cadence: 'daily',
      desc: 'Your standup update, pre-written.',
      connectors: ['GitHub', 'Jira', 'Slack'],
      steps: [
        'Pull my commits from yesterday and my assigned Jira tickets',
        'Draft my standup (Yesterday / Today / Blockers)',
        'Post it to Slack for me to confirm'
      ],
      prompt: 'Save a recipe called Standup Bot: every weekday at 9:15am, pull my commits from yesterday and my assigned Jira tickets, draft my standup (Yesterday / Today / Blockers), and post it to Slack for me to confirm.'
    },
    {
      id: 'release', title: 'Release Notes', group: 'dev', cadence: 'trigger',
      desc: 'The changelog writes itself.',
      connectors: ['GitHub', 'Drive', 'Slack'],
      steps: [
        'Gather every merged PR since the last tag',
        'Draft a clean changelog grouped by feature / fix',
        'Save it to Google Drive',
        'Post the highlights to Slack'
      ],
      prompt: 'Save a recipe called Release Notes: whenever we cut a release, gather every merged PR since the last tag, draft a clean changelog grouped by feature and fix, save it to Google Drive, and post the highlights to Slack.'
    },
    {
      id: 'xd-design-template', title: 'Code to the Design Template', group: 'dev', cadence: 'trigger', live: true,
      desc: 'New code written to your architecture doc — planned with you first, checked against the rules after, and never committed for you.',
      connectors: ['Drive', 'GitHub'],
      steps: [
        'Read the design-template doc from Google Drive',
        'Confirm the target repository and report which branch is checked out',
        'Restate the requirement, name the layer each piece belongs in, and list every file that will be created or changed',
        'Check whether an existing service already covers the domain before adding a new one',
        'Flag whether the operation is sensitive under the template, and say what the audit record will carry',
        'Present that plan and wait for agreement before writing a single file',
        'Write the code to the template — never a real credential in source, tests, fixtures or .env, only an obvious placeholder',
        "Run the template's own self-check against the new code, then report which rules were applied and which were deliberately skipped, with a reason for each",
        'Show every file created or changed, and hand off to the code-review recipe — never commit, push, branch or merge'
      ],
      prompt: "Save a recipe called Code to the Design Template: read our design-template doc from Google Drive, confirm the target repo and report the checked-out branch. Restate the requirement, name the layer each piece belongs in, and list every file you'll create or change. Check whether an existing service already covers the domain. Flag whether the operation is sensitive under the template and say what the audit record will carry. Present that plan and wait for my agreement before writing a single file. Then write the code to the template — never a real credential in source, tests, fixtures or .env, only an obvious placeholder. Run the template's own self-check, report which rules you applied and which you deliberately skipped with reasons, show every file changed, and offer to run my code-review recipe. Never commit, push, branch or merge."
    },
    {
      id: 'xd-code-review', title: 'Standards Code Review', group: 'dev', cadence: 'trigger', live: true,
      desc: 'Every pull request checked against your written engineering standards before a human opens it.',
      connectors: ['GitHub', 'Drive', 'Slack'],
      steps: [
        'Confirm which branch or PR to review — the base is always the staging branch',
        'Read the coding-standards doc in Google Drive and follow its rule set exactly',
        'Pull the diff read-only and review only the lines this branch changed',
        'Check every MUST rule and record the rule id, file:line, the snippet and the fix — flag any real credential to rotate immediately',
        'Collect the SHOULD rules separately as advisory notes',
        'Give a verdict: APPROVED, CHANGES REQUESTED or NEEDS DISCUSSION',
        'Post the verdict, branch, commit and blocking count to the review Slack channel, with the full findings in a thread',
        'Hand back the same review as a ready-to-paste GitHub comment — never approve, merge or push'
      ],
      prompt: 'Save a recipe called Standards Code Review: when I give you a branch or PR (base is always the staging branch), read our coding-standards doc in Google Drive, pull the diff read-only and review only the changed lines. Check every MUST rule and record the rule id, file:line, snippet and fix — flag any real credential to rotate. Collect SHOULD rules as advisory notes. Give a verdict of APPROVED, CHANGES REQUESTED or NEEDS DISCUSSION, post it to our code-review Slack channel with the findings in a thread, and give me the same review as a ready-to-paste GitHub comment. Never approve, merge or push.'
    },
    {
      id: 'devops-screen', title: 'DevOps Resume Screen', group: 'hiring', cadence: 'trigger', live: true,
      desc: 'Every candidate measured against the same written bar — so the shortlist is consistent and a human still makes every call.',
      connectors: ['Drive', 'Slack', 'Memory'],
      steps: [
        'Read the resume end-to-end and pull out the facts: experience, work history, scope of ownership',
        'Apply the same written must-have criteria to every candidate, pass/fail, with the reasoning shown',
        'List the good-to-haves actually evidenced in the CV, not just claimed',
        'Rate depth in the core skill — None, Shallow, Moderate or Deep — and say what evidence supports it',
        'Note what should be verified in a conversation rather than assumed from paper',
        'Produce a recommendation with the full reasoning attached',
        'Share the assessment with the hiring team so the decision gets discussed, never applied automatically',
        'Offer tailored interview probes so everyone who moves forward gets a fair, specific conversation'
      ],
      prompt: "Save a recipe called DevOps Resume Screen: when I hand you a resume, read it end-to-end and pull out the facts — experience, work history, scope of ownership. Apply my written must-have criteria to every candidate the same way, pass/fail with reasoning shown, and list the good-to-haves actually evidenced. Rate depth in the core skill as None, Shallow, Moderate or Deep and say what supports it. Note what should be verified in conversation rather than assumed from paper. Produce a recommendation with the full reasoning attached, share it with my hiring team for discussion, and offer tailored interview probes for anyone moving forward. A human makes every call."
    },
    {
      id: 'product-screen', title: 'Product Support Resume Screen', group: 'hiring', cadence: 'trigger', live: true,
      desc: 'The same structured screen, tuned for support roles — including real hands-on payments exposure.',
      connectors: ['Drive', 'Slack', 'Memory'],
      steps: [
        'Take resumes one at a time or as a batch from a folder',
        'Read each one end-to-end and pull out the facts',
        'Apply the same written must-have criteria to every candidate, pass/fail, with the reasoning shown',
        'List the good-to-haves actually evidenced in the CV',
        'Assess genuine hands-on payments exposure rather than keyword matches',
        'Note what should be verified in a conversation rather than assumed from paper',
        'Produce a recommendation with the full reasoning attached',
        'Share the assessment with the hiring team so the decision gets discussed, never applied automatically',
        'Offer interview probes for everyone who moves forward'
      ],
      prompt: "Save a recipe called Product Support Resume Screen: take resumes one at a time or as a batch from a folder. Read each end-to-end and pull out the facts. Apply my written must-have criteria to every candidate the same way, pass/fail with reasoning shown, and list the good-to-haves actually evidenced. Assess genuine hands-on payments exposure rather than keyword matches. Note what should be verified in conversation rather than assumed from paper. Produce a recommendation with the full reasoning attached, share it with my hiring team for discussion, and offer interview probes for anyone moving forward. A human makes every call."
    },
    {
      id: 'invoice-stats', title: 'Invoice Stats & Alert', group: 'fintech', cadence: 'weekly',
      desc: 'Watch your last invoices for anything off.',
      connectors: ['Stripe', 'Memory', 'Slack'],
      steps: [
        'Pull my last invoices from Stripe',
        'Compare the most recent 3 against the prior 3 (amount, paid vs outstanding, days-to-pay)',
        'Flag anything unusual',
        'Save the summary to my memory and post it to Slack'
      ],
      prompt: 'Save a recipe called Invoice Stats: every Monday, pull my last invoices from Stripe, compare the most recent 3 against the prior 3 (amount, paid vs outstanding, average days-to-pay), flag anything unusual, save the summary to my memory, and post it to Slack.'
    },
    {
      id: 'dunning', title: 'Overdue Chaser', group: 'fintech', cadence: 'daily',
      desc: 'Collect what’s owed, politely.',
      connectors: ['Stripe', 'Gmail', 'Memory'],
      steps: [
        'List overdue Stripe invoices',
        'Draft a friendly reminder email for each',
        'Escalate the tone after 7 and 14 days',
        'Log every reminder I send to memory'
      ],
      prompt: 'Save a recipe called Overdue Chaser: every morning, list overdue Stripe invoices, draft a friendly reminder email for each, escalate the tone after 7 and 14 days, and log every reminder I send to my memory.'
    },
    {
      id: 'reconcile', title: 'Payment Reconciliation', group: 'fintech', cadence: 'monthly',
      desc: 'Match every payment to an invoice.',
      connectors: ['Stripe', 'Drive', 'Memory'],
      steps: [
        'Match Stripe payments against my invoices',
        'Flag any mismatches or missing payments',
        'Build a reconciliation report in Google Drive',
        'Store the totals in my memory'
      ],
      prompt: 'Save a recipe called Reconciliation: at month-end, match Stripe payments against my invoices, flag any mismatches or missing payments, build a reconciliation report in Google Drive, and store the totals in my memory.'
    }
  ];

  get filteredRecipes(): Recipe[] {
    if (this.activeCategory === 'all') { return this.recipes; }
    if (this.activeCategory === 'live') { return this.recipes.filter(r => r.live); }
    return this.recipes.filter(r => r.group === this.activeCategory);
  }

  get liveCount(): number { return this.recipes.filter(r => r.live).length; }

  demoLink(title: string): string {
    return `mailto:${this.demoEmail}?subject=${encodeURIComponent('Demo request: ' + title)}`;
  }

  setCategory(key: string) { this.activeCategory = key; }
  toggle(id: string) { this.expandedId = this.expandedId === id ? '' : id; }

  cadLabel(c: string): string {
    return { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', trigger: 'On a trigger' }[c] || c;
  }

  copyPrompt(id: string, text: string, ev: Event) {
    ev.stopPropagation();
    navigator.clipboard?.writeText(text).then(() => {
      this.copiedId = id;
      setTimeout(() => { if (this.copiedId === id) this.copiedId = ''; }, 1800);
    });
  }
}
